/**
 * `<NotePoolMetrics>` — the editor's empty-state corpus dashboard.
 *
 * Shown (full-bleed, no editor chrome) when there's NO note selected
 * (`noteId === null`). Instead of a blank editor it surfaces a live snapshot of
 * the whole corpus, following the Search / Chat surface design language: a
 * `.page-head` header, a corpus-stat KPI strip, and a grid of `.note-metrics__card`
 * panels using the shared `.label` / `.chip` primitives and design tokens.
 *
 * Every datum comes from an existing, verified hook — composition only, no new
 * endpoints:
 *   - headline counts          → useSystemStatusQuery
 *   - top tags / top entities  → useTagListQuery / useEntityListQuery
 *   - open tasks               → useActionItemsQuery (corpus scope)
 *   - contradictions           → useContradictionsQuery (corpus scope)
 *   - write cadence + recent   → useScopedNotesQuery (corpus scope)
 *   - entity spotlight         → useEntityDetailQuery (the top entity)
 *
 * Styles live in `noteMetrics.css`.
 */

import { useMemo, type ReactNode } from 'react';
import {
  useSystemStatusQuery,
  useTagListQuery,
  useEntityListQuery,
  useActionItemsQuery,
  useContradictionsQuery,
  useScopedNotesQuery,
  useEntityDetailQuery,
  useNavigateToNote,
  type DashboardScope,
} from '@/hooks';
import type { Entity, Tag, ResultItem, ActionItemView } from '@/types';
import { NotesTable } from './NotesTable';
import './noteMetrics.css';

/** How many rows to surface in a top-N panel. */
const TOP_N = 7;
/** Minimum note count for a tag / entity to be "interesting" enough to list. */
const MIN_COUNT = 2;
/** Number of trailing weekly buckets in the write-cadence sparkline. */
const CADENCE_WEEKS = 14;
const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

/** A whole-corpus, all-time scope — the dashboard isn't date- or notebook-scoped. */
const CORPUS_SCOPE: DashboardScope = {
  dateRange: { start: null, end: null },
  scope: { kind: 'corpus', ref: null },
};

/**
 * The notes-window scope feeding the cadence + recent-notes cards. Unlike the
 * action-item / contradiction queries (which accept an empty filter), the
 * `search_by_metadata` backing `useScopedNotesQuery` REJECTS a fully-unfiltered
 * request (422). A wide [2000 → 2100] date window gives it a valid filter that
 * still captures the entire corpus.
 */
const ALL_TIME_NOTES_SCOPE: DashboardScope = {
  dateRange: { start: '2000-01-01', end: '2100-01-01' },
  scope: { kind: 'corpus', ref: null },
};

export function NotePoolMetrics(): JSX.Element {
  const statusQuery = useSystemStatusQuery();
  const { items: tagItems } = useTagListQuery({ minCount: MIN_COUNT });
  const { items: entityItems } = useEntityListQuery({ minCount: MIN_COUNT, sort: 'count' });
  const actionItemsQuery = useActionItemsQuery(CORPUS_SCOPE);
  const contradictionsQuery = useContradictionsQuery(CORPUS_SCOPE);
  // limit 100 = SEARCH_LIMIT_MAX (the backend rejects larger pages); enough to
  // drive the recent-notes list and a representative cadence sparkline.
  const scopedNotesQuery = useScopedNotesQuery(ALL_TIME_NOTES_SCOPE, 100);
  const goToNote = useNavigateToNote();

  const status = statusQuery.data;

  const topTags = useMemo<Tag[]>(
    () => [...tagItems].sort((a, b) => b.noteCount - a.noteCount).slice(0, TOP_N),
    [tagItems],
  );
  const topEntities = useMemo<Entity[]>(
    () => [...entityItems].sort((a, b) => b.noteCount - a.noteCount).slice(0, TOP_N),
    [entityItems],
  );

  // The top entity anchors the "spotlight" card — its co-occurring entities.
  const topEntity = topEntities[0] ?? null;
  const entityDetailQuery = useEntityDetailQuery(topEntity?.entityId ?? null);

  // Open action items (status === 'open'), soonest-due first (undated last).
  const openTasks = useMemo<ActionItemView[]>(() => {
    const items = actionItemsQuery.data?.items ?? [];
    return items
      .filter((a) => a.status === 'open')
      .sort((a, b) => dueRank(a.dueDate) - dueRank(b.dueDate));
  }, [actionItemsQuery.data]);

  const contradictionCount = contradictionsQuery.data?.items.length ?? 0;

  const scopedNotes = useMemo<ResultItem[]>(
    () => scopedNotesQuery.data?.items ?? [],
    [scopedNotesQuery.data],
  );

  // Write cadence — notes created per trailing week (oldest → newest).
  const cadence = useMemo(() => buildCadence(scopedNotes), [scopedNotes]);
  const cadenceMax = cadence.reduce((m, c) => Math.max(m, c.count), 0);
  const cadenceTotal = cadence.reduce((s, c) => s + c.count, 0);

  const maxTagCount = topTags[0]?.noteCount ?? 0;
  const maxEntityCount = topEntities[0]?.noteCount ?? 0;
  const cooccurring = entityDetailQuery.data?.topCooccurring ?? [];

  return (
    <div className="note-metrics">
      <header className="page-head">
        <div className="page-head__main">
          <div className="page-head__eyebrow">WORKSPACE</div>
          {/* All one foreground colour (no accent word) per Chris — reads as plain white
              on the dark themes. */}
          <h1 className="page-title">my notes</h1>
          <p className="page-sub">
            A live overview of everything in your library. Pick a note from the
            tree, or create one, to start writing.
          </p>
        </div>
      </header>

      <div className="note-metrics__body">
        {/* PRIMARY — the interactive notes table (sort + filter + infinite scroll). The point of
            the snapshot is "find the note I want"; the analytics live below it. */}
        <section className="note-metrics__recent">
          <NotesTable />
        </section>

        {/* ANALYTICS — demoted below the table: headline counts + the cards. */}
        <div className="note-metrics__kpis">
          <Kpi label="Notes" value={status?.totalLiveNotes} />
          <Kpi label="Chunks" value={status?.totalChunks} />
          <Kpi label="Tags" value={status?.totalTags} />
          <Kpi label="Entities" value={status?.totalEntities} />
          <Kpi label="Open tasks" value={openTasks.length} tone="accent" />
          <Kpi label="Conflicts" value={contradictionCount} tone="accent" />
        </div>

        <div className="note-metrics__grid">
          {/* Write cadence sparkline */}
          <Card title="Write cadence" count={`${cadenceTotal} notes`}>
            {cadenceTotal === 0 ? (
              <Empty>No dated notes in the corpus yet.</Empty>
            ) : (
              <div
                className="note-metrics__spark"
                role="img"
                aria-label={`Notes created over the last ${CADENCE_WEEKS} weeks`}
              >
                {cadence.map((c, i) => (
                  <span
                    key={i}
                    className="note-metrics__spark-bar"
                    style={{
                      height: `${cadenceMax > 0 ? Math.max(6, Math.round((c.count / cadenceMax) * 100)) : 0}%`,
                    }}
                    title={`${c.label}: ${c.count} note${c.count === 1 ? '' : 's'}`}
                  />
                ))}
              </div>
            )}
            <div className="note-metrics__spark-axis">
              <span>{CADENCE_WEEKS}w ago</span>
              <span>this week</span>
            </div>
          </Card>

          {/* Top tags */}
          <Card title="Top tags" count={`${topTags.length}`}>
            {topTags.length === 0 ? (
              <Empty>No tags yet — they appear as notes are extracted.</Empty>
            ) : (
              <div className="note-metrics__rows">
                {topTags.map((t) => (
                  <Bar
                    key={t.tagId}
                    name={`#${t.display}`}
                    count={t.noteCount}
                    max={maxTagCount}
                  />
                ))}
              </div>
            )}
          </Card>

          {/* Top entities */}
          <Card title="Top entities" count={`${topEntities.length}`}>
            {topEntities.length === 0 ? (
              <Empty>No entities yet — they appear as notes are extracted.</Empty>
            ) : (
              <div className="note-metrics__rows">
                {topEntities.map((e) => (
                  <Bar
                    key={e.entityId}
                    badge={e.type.slice(0, 2).toUpperCase()}
                    name={e.canonical}
                    count={e.noteCount}
                    max={maxEntityCount}
                  />
                ))}
              </div>
            )}
          </Card>

          {/* Outstanding action items */}
          <Card title="Outstanding tasks" count={`${openTasks.length}`}>
            {openTasks.length === 0 ? (
              <Empty>No open action items. Inbox zero.</Empty>
            ) : (
              <div className="note-metrics__rows">
                {openTasks.slice(0, 6).map((a) => (
                  <button
                    key={a.actionItemId}
                    type="button"
                    className="note-metrics__task"
                    onClick={() => goToNote({ noteId: a.sourceNoteId })}
                    title="Open the source note"
                  >
                    <span className="note-metrics__task-text">{a.text}</span>
                    {a.dueDate !== null && (
                      <span className="note-metrics__task-due">
                        {formatDue(a.dueDate)}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </Card>

          {/* Entity spotlight — the top entity's co-occurrence neighborhood */}
          <Card
            title="Entity spotlight"
            count={topEntity ? `${entityDetailQuery.data?.mentionCount ?? topEntity.noteCount} mentions` : undefined}
          >
            {topEntity === null ? (
              <Empty>No entities yet.</Empty>
            ) : (
              <div className="note-metrics__spotlight">
                <div className="note-metrics__spotlight-head">
                  <span className="note-metrics__spotlight-name">
                    {topEntity.canonical}
                  </span>
                  <span className="chip chip--cool">{topEntity.type}</span>
                </div>
                {cooccurring.length === 0 ? (
                  <Empty>
                    {entityDetailQuery.isLoading
                      ? 'Loading co-occurrences…'
                      : 'No co-occurring entities recorded.'}
                  </Empty>
                ) : (
                  <>
                    <div className="note-metrics__spotlight-label">
                      Appears most with
                    </div>
                    <div className="note-metrics__chips">
                      {cooccurring.slice(0, 8).map((c) => (
                        <span key={c.entityId} className="chip">
                          {c.canonical}
                          <span className="note-metrics__chip-count">
                            {c.cooccurrenceCount}
                          </span>
                        </span>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
          </Card>
        </div>

        {/* Corpus health footer */}
        <div className="note-metrics__footer">
          <span className="note-metrics__footer-item">
            Last extraction{' '}
            <strong>
              {status?.lastExtractionAt ? formatRelative(status.lastExtractionAt) : '—'}
            </strong>
          </span>
          {status?.activeInferenceProvider !== undefined && (
            <span className="note-metrics__footer-item">
              Inference <strong>{status.activeInferenceProvider}</strong>
            </span>
          )}
          {status?.backendVersion !== undefined && (
            <span className="note-metrics__footer-item">
              Backend <strong>{status.backendVersion}</strong>
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

/** One KPI tile — reuses the corpus-stat visual (display number + mono label). */
function Kpi({
  label,
  value,
  tone,
}: {
  label: string;
  value: number | undefined;
  tone?: 'accent';
}): JSX.Element {
  return (
    <div className="note-metrics__kpi">
      <span
        className={
          'note-metrics__kpi-v' +
          (value === undefined ? ' note-metrics__kpi-v--muted' : '') +
          (tone === 'accent' && (value ?? 0) > 0 ? ' note-metrics__kpi-v--accent' : '')
        }
      >
        {value === undefined ? '—' : value.toLocaleString()}
      </span>
      <span className="note-metrics__kpi-k">{label}</span>
    </div>
  );
}

/** A panel card following the Search/Chat card language. */
function Card({
  title,
  count,
  children,
}: {
  title: string;
  count?: string;
  children: ReactNode;
}): JSX.Element {
  return (
    // `card` = the shared card primitive (app.css); `note-metrics__card` keeps
    // only this surface's deltas (soft hairline, tighter radius, column flow).
    <section className="card note-metrics__card">
      <div className="note-metrics__card-head">
        <span className="label">
          {title}
          {/* U7: "· N", not "·N" — the glued middot read as a minus sign. */}
          {count !== undefined && <span className="label-count">· {count}</span>}
        </span>
      </div>
      {children}
    </section>
  );
}

/** A proportional top-N row (tag/entity). */
function Bar({
  name,
  count,
  max,
  badge,
}: {
  name: string;
  count: number;
  max: number;
  badge?: string;
}): JSX.Element {
  const pct = max > 0 ? Math.max(6, Math.round((count / max) * 100)) : 0;
  return (
    <div className="note-metrics__row">
      {badge !== undefined && <span className="note-metrics__row-badge">{badge}</span>}
      <span className="note-metrics__row-name" title={name}>
        {name}
      </span>
      <span className="note-metrics__row-bar">
        <span className="note-metrics__row-fill" style={{ width: `${pct}%` }} />
      </span>
      <span className="note-metrics__row-count">{count.toLocaleString()}</span>
    </div>
  );
}

function Empty({ children }: { children: ReactNode }): JSX.Element {
  return <div className="note-metrics__empty">{children}</div>;
}

// --- helpers ----------------------------------------------------------------

/** Bucket notes by trailing week of `createdAt` (oldest → newest). */
function buildCadence(notes: ResultItem[]): { label: string; count: number }[] {
  const now = Date.now();
  const counts = new Array<number>(CADENCE_WEEKS).fill(0);
  for (const n of notes) {
    const t = Date.parse(n.createdAt);
    if (Number.isNaN(t)) continue;
    const weeksAgo = Math.floor((now - t) / WEEK_MS);
    if (weeksAgo >= 0 && weeksAgo < CADENCE_WEEKS) {
      counts[CADENCE_WEEKS - 1 - weeksAgo] += 1;
    }
  }
  return counts.map((count, i) => {
    const weeksAgo = CADENCE_WEEKS - 1 - i;
    return { label: weeksAgo === 0 ? 'this week' : `${weeksAgo}w ago`, count };
  });
}

/** Sort key for due dates: dated items first (by date), undated last. */
function dueRank(due: string | null): number {
  if (due === null) return Number.MAX_SAFE_INTEGER;
  const t = Date.parse(due);
  return Number.isNaN(t) ? Number.MAX_SAFE_INTEGER : t;
}

/** Compact relative-time formatter (matches the editor's meta row). */
function formatRelative(iso: string): string {
  try {
    const diffMs = Date.now() - new Date(iso).getTime();
    if (diffMs < 60_000) return 'just now';
    const m = Math.round(diffMs / 60_000);
    if (m < 60) return `${m}m ago`;
    const h = Math.round(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.round(h / 24);
    if (d < 7) return `${d}d ago`;
    if (d < 30) return `${Math.round(d / 7)}w ago`;
    if (d < 365) return `${Math.round(d / 30)}mo ago`;
    return new Date(iso).toLocaleDateString();
  } catch {
    return iso;
  }
}

/** Due-date label: "overdue", "today", "in Nd", or a short date. */
function formatDue(iso: string): string {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return iso;
  const days = Math.round((t - Date.now()) / (24 * 60 * 60 * 1000));
  if (days < 0) return 'overdue';
  if (days === 0) return 'today';
  if (days === 1) return 'tomorrow';
  if (days < 14) return `in ${days}d`;
  return new Date(t).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}
